
import React, { useState, useEffect } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { IoClose } from "react-icons/io5";
import { API_BASE_URL } from "../../../../config";

const AddCustomerForm = ({ onClose }) => {
  const fieldMap = {
    "Lead Date*": "leadDate",
    "Lead From*": "leadFrom",
    "Customer Name*": "customerName",
    "Customer Number*": "customerNumber",
    "Customer Address*": "customerAddress",
    "Required Flat Type*": "requiredFlatType",
    "Location Required*": "locationRequired",
    "Required Property Type*": "requiredResaleOrNewFlat",
    "Budget*": "budget",
    "Customer Feedback Category*": "customerFeedbackCategory",
    "Follow-up Date*": "followupDate",
    "Call Type*": "callType",
    "Parking*": "parking",
    "Client Feedback*": "clientFeedback",
    "Added By* ": "addedBy",
  };

  // const [formData, setFormData] = useState(
  //   Object.fromEntries(Object.values(fieldMap).map((key) => [key, ""]))
  // );

  const adminId = localStorage.getItem("AdminId");

  const [formData, setFormData] = useState({
    ...Object.fromEntries(Object.values(fieldMap).map((key) => [key, ""])),
    addedBy: adminId || "",
  });

  // ✅ extra fields (API me naye aaye hai)
  const [extraFields, setExtraFields] = useState({
    callBackDateTime: "",
    visitScheduleDateTime: "",
    otherFeedbackText: "",
  });

  const [admins, setAdmins] = useState([]);

  // ✅ Validation state
  const [errors, setErrors] = useState({
    customerNumber: "",
    customerAddress: "",
    budget: "",
  });

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE_URL}/api/admin/get-all-subadmin`,
          { headers: { token } }
        );
        if (res.data.success) {
          setAdmins(res.data.result.subAdmins || []);
        }
      } catch (err) {
        // console.error("Failed to load admins", err);
      }
    };
    fetchAdmins();
  }, []);

  // ✅ Format budget as currency (Indian style)
  const formatBudget = (value) => {
    if (!value) return "";
    const num = value.replace(/\D/g, ""); // remove non-digits
    if (!num) return "";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    let newValue = type === "checkbox" ? e.target.checked : value;

    // ✅ Customer Number validation
    if (name === "customerNumber") {
      if (/^\d*$/.test(newValue)) {
        setErrors((prev) => ({ ...prev, customerNumber: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          customerNumber: "Customer number must contain digits only.",
        }));
      }
    }

    // ✅ Customer Address validation
    if (name === "customerAddress") {
      const regex = /^[a-zA-Z0-9\s,.\-/#()]+$/;
      if (newValue && !regex.test(newValue)) {
        setErrors((prev) => ({
          ...prev,
          customerAddress:
            "Invalid address. Only letters, numbers, spaces & ,.-/#() allowed.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, customerAddress: "" }));
      }
    }

    // ✅ Budget formatting
    if (name === "budget") {
      const digitsOnly = newValue.replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));

      setErrors((prev) => ({ ...prev, budget: "" }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleExtraChange = (e) => {
    const { name, value } = e.target;
    setExtraFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Prevent submission if validation errors
    if (errors.customerNumber || errors.customerAddress) {
      toast.error("Please fix validation errors before saving.");
      return;
    }

    const token = localStorage.getItem("token");
    const loadingToast = toast.loading("Saving customer...");

    try {

      const leadDateNew = new Date(formData.leadDate).toISOString();

      const followupDateNew = new Date(formData.followupDate).toISOString();

      const payload = { ...formData, ...extraFields, leadDate: leadDateNew, followupDate: followupDateNew };

      const response = await axios.post(
        `${API_BASE_URL}/api/customer/add-customer`,
        payload,
        { headers: { token } }
      );

      toast.dismiss(loadingToast);
      if (response.data.success) {
        toast.success("Customer added successfully!");
        setTimeout(onClose, 1500);
      } else {
        toast.error("Error: " + response.data.message);
      }
    } catch (err) {
      toast.dismiss(loadingToast);
      // console.error(err);
      toast.error("Failed to add customer.");
    }
  };

  return (
    <div className="bg-black fixed inset-0 z-50 flex justify-center overflow-y-auto bg-opacity-40 backdrop-blur-sm">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="mb-10 mt-10 max-h-[calc(100vh-40px)] w-full max-w-full overflow-y-auto rounded-lg bg-white p-4 shadow-xl dark:bg-navy-800 sm:max-w-5xl sm:p-6">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between sm:mb-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white sm:text-2xl">
            Add New Customer
          </h2>
          <button
            className="rounded bg-red-700 px-2 py-2 text-white transition hover:bg-red-800"
            onClick={onClose}
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Form */}
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3"
          onSubmit={handleSubmit}
        >
          {Object.keys(fieldMap).map((label, idx) => {
            const key = fieldMap[label];
            const isDateField = key === "leadDate" || key === "followupDate";

            return (
              <div key={idx} className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                  {label}
                </label>

                {key === "requiredResaleOrNewFlat" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Option</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Resale">Resale</option>
                    <option value="New">New</option>
                    <option value="Both">Both</option>
                  </select>
                ) : key === "callType" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Option</option>
                    <option value="Fresh Call">Fresh Call</option>
                    <option value="Re-call">Re-call</option>
                  </select>
                ) : key === "parking" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Option</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                ) : key === "customerFeedbackCategory" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Option</option>
                    <option value="Ringing/Busy">Ringing/Busy</option>
                    <option value="Call back">Call back</option>
                    <option value="Need With Cost Sheet Share">Need With Cost Sheet Share</option>
                    <option value="Cost Sheet Shared">Cost Sheet Shared</option>
                    <option value="Visit schedule">Visit schedule</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Booking Done">Booking Done</option>
                    <option value="Visit Done">Visit Done</option>
                    <option value="Other">Other</option>
                  </select>
                ) : key === "addedBy" ? (
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    disabled
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select Admin</option>
                    {admins.map((admin) => (
                      <option key={admin._id} value={admin._id}>
                        {admin.adminName}
                      </option>
                    ))}
                  </select>
                ) : key === "budget" ? (
                  <>
                    <input
                      type="text"
                      name={key}
                      value={formatBudget(formData[key])}
                      onChange={handleChange}
                      placeholder="Enter Budget"
                      className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                    />
                    {errors.budget && (
                      <span className="text-xs text-red-500">
                        {errors.budget}
                      </span>
                    )}
                  </>
                ) : (
                  <input
                    type={isDateField ? "datetime-local" : "text"}
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    placeholder={`Enter ${label}`}
                    className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  />
                )}

                {/* ✅ Inline error messages */}
                {key === "customerNumber" && errors.customerNumber && (
                  <span className="text-xs text-red-500">
                    {errors.customerNumber}
                  </span>
                )}
                {key === "customerAddress" && errors.customerAddress && (
                  <span className="text-xs text-red-500">
                    {errors.customerAddress}
                  </span>
                )}
              </div>
            );
          })}

          {/* ✅ Conditional fields */}
          {formData.customerFeedbackCategory === "Call back" && (
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Call Back Date & Time*
              </label>
              <input
                type="datetime-local"
                name="callBackDateTime"
                value={extraFields.callBackDateTime}
                onChange={handleExtraChange}
                className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

          {formData.customerFeedbackCategory === "Visit schedule" && (
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Visit Schedule Date & Time*
              </label>
              <input
                type="datetime-local"
                name="visitScheduleDateTime"
                value={extraFields.visitScheduleDateTime}
                onChange={handleExtraChange}
                className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

          {formData.customerFeedbackCategory === "Other" && (
            <div className="col-span-full flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Other Feedback*
              </label>
              <input
                type="text"
                name="otherFeedbackText"
                value={extraFields.otherFeedbackText}
                onChange={handleExtraChange}
                placeholder="Enter feedback details"
                className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

          {formData.customerFeedbackCategory === "Need With Cost Sheet Share" && (
            <div className="col-span-full flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Notes*
              </label>
              <input
                type="text"
                name="otherFeedbackText"
                value={extraFields.otherFeedbackText}
                onChange={handleExtraChange}
                placeholder="Enter notes"
                className="rounded-md border px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
              />
            </div>
          )}

          {/* Submit button */}
          <div className="col-span-full mt-4 flex justify-end sm:mt-6">
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 sm:text-base"
            >
              Save Customer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerForm;
